import * as React from "react";
import {
  isTestMailTemplate,
  TEST_MAIL_TEMPLATES,
  TEST_MAILS_PER_HOUR,
  TEST_SUBJECT_PREFIX,
  type TestMailTemplate,
} from "@ewn/shared";
import { sendTestMail } from "@/api/admin";
import { useAction } from "@/lib/useAsync";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Select } from "@/components/ui/input";
import { Notice } from "@/components/ui/feedback";

/** The picker's words for each template; the list is `@ewn/shared`'s. */
const LABEL: Record<TestMailTemplate, string> = {
  order_code: "Order code (after checkout)",
  underpaid: "Partly paid (crypto arrived short)",
  delivery: "eSIM ready (delivery)",
  topup: "Top-up ready",
};

/**
 * A customer's message, sent to the operator with sample values.
 *
 * "Recorded" is reported as what it is: with no mail provider configured
 * nothing reaches an inbox, and saying "sent" there would send the operator
 * looking for a message that does not exist.
 */
export function TestMailCard() {
  const [template, setTemplate] = React.useState<TestMailTemplate>("delivery");
  const [to, setTo] = React.useState("");
  const [result, setResult] = React.useState<string | null>(null);
  const action = useAction();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setResult(null);
    await action.run(async () => {
      const answer = await sendTestMail(template, to.trim());
      setResult(
        answer.delivered
          ? `Sent to ${to.trim()}. The subject starts with ${TEST_SUBJECT_PREFIX.trim()}.`
          : "Recorded, not sent: no mail provider is configured.",
      );
      return answer;
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Send a test email</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-secondary">
            One of the messages customers receive, filled with sample values. Up to{" "}
            {TEST_MAILS_PER_HOUR} an hour.
          </p>
          <Field label="Email" htmlFor="test-mail-template">
            <Select
              id="test-mail-template"
              value={template}
              onChange={(e) => {
                if (isTestMailTemplate(e.target.value)) setTemplate(e.target.value);
              }}
            >
              {TEST_MAIL_TEMPLATES.map((key) => (
                <option key={key} value={key}>
                  {LABEL[key]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Send to" htmlFor="test-mail-to">
            <Input
              id="test-mail-to"
              type="email"
              required
              placeholder="you@example.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Field>
          {action.error && <Notice tone="error">{action.error}</Notice>}
          {result && <Notice tone="success">{result}</Notice>}
          <Button type="submit" disabled={action.busy || to.trim() === ""}>
            {action.busy ? "Sending…" : "Send test"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
